import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { useGetTranslatorsQuery, useCreateTranslatorMutation } from "state/api";
import Header from "components/Header";
import ActionsMenu from "components/ActionsMenu";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import Spinner from "components/Spinner";
import { useSnackbar } from "components/Snackbar";
import { capitalizeFirstLetter } from "components/usefulFunctions";

const Translators = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [translatorExists, setTranslatorExists] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [filterModel, setFilterModel] = useState({ items: [] });
  const { data, isLoading, refetch } = useGetTranslatorsQuery({
    page,
    pageSize,
    sort: JSON.stringify(sort),
    search,
    pending: false,
    filter: JSON.stringify(filterModel.items),
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [createTranslatorMutation] = useCreateTranslatorMutation();
  const [newTranslatorData, setNewTranslatorData] = useState({
    name: "",
    email: "",
  });
  const [columnsVisibility, setColumnsVisibility] = useState({
    name: true,
    email: true,
    phoneNumber: true,
    languages: true,
    targetLanguages: true,
    specialty: true,
    country: true,
    services: true,
    trusted: true,
  });

  // Fetch jobs when component mounts and whenever page, pageSize, sort, or search changes
  useEffect(() => {
    refetch();
  }, [page, pageSize, sort, search, refetch]);

  // Refetch jobs when the page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup the event listener when the component unmounts
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refetch]);

  const handleDialogConfirm = async () => {
    try {
      if (!translatorExists) {
        // Check if name exists already
        setSearch(newTranslatorData.name);
        const refetchResult = await refetch();

        if (
          refetchResult.data.vendors.length > 0 &&
          refetchResult.data.vendors.some(
            (vendor) =>
              vendor.name.toLowerCase() === newTranslatorData.name.toLowerCase()
          )
        ) {
          // Translator already exists, show warning and confirm again
          setTranslatorExists(true);
          return;
        }
      }

      // If translator does not exist or user confirms again, create the translator
      const createResult = await createTranslatorMutation({
        name: newTranslatorData.name,
        email: newTranslatorData.email,
      });

      if (createResult.data.translatorId) {
        navigate("/translators/" + createResult.data.translatorId);
      }

      setOpenDialog(false);
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const handleDialogClose = () => {
    setTranslatorExists(false);
    setOpenDialog(false);
  };

  const actions = [
    {
      label: "Create New Translator",
      onClick: () => setOpenDialog(true),
    },
  ];

  const columns = [
    { field: "name", headerName: "Name", flex: 0.5, type: "string" },
    { field: "email", headerName: "Email", flex: 0.8, type: "string" },
    {
      field: "phoneNumber",
      headerName: "Phone Number",
      flex: 0.5,
      type: "string",
    },

    {
      field: "languages",
      headerName: "Source Languages",
      flex: 1,
      type: "string",
      valueGetter: (params) => {
        const all = params.row.languages || [];
        const src = all
          .flatMap((l) =>
            (l?.sourceLanguages || []).map((s) => s?.name).filter(Boolean)
          )
          .filter(Boolean)
          .join(", ");
        return src || "";
      },
      renderCell: ({ value }) => <Box>{value}</Box>,
    },

    {
      field: "targetLanguages",
      headerName: "Target Languages",
      flex: 1,
      type: "string",
      valueGetter: (params) => {
        const all = params.row.languages || [];
        const tgt = all
          .map((l) => l?.targetLanguage?.name)
          .filter(Boolean)
          .join(", ");
        return tgt || "";
      },
      renderCell: ({ value }) => <Box>{value}</Box>,
    },

    {
      field: "services",
      headerName: "Services",
      flex: 0.7,
      type: "string",
      valueGetter: (params) => (params.value || []).join(", "),
      renderCell: ({ value }) => <Box>{capitalizeFirstLetter(value)}</Box>,
    },

    {
      field: "specialty",
      headerName: "Specialty",
      flex: 0.7,
      type: "string",
      valueGetter: (params) => (params.value || []).join(", "),
      renderCell: ({ value }) => <Box>{capitalizeFirstLetter(value)}</Box>,
    },

    {
      field: "rates",
      headerName: "Rates",
      flex: 0.7,
      type: "string",
      valueGetter: (params) => {
        const currency = params.row?.preferredCurrency || "";
        const rates = params.row?.rates || [];
        return rates
          .map((r) => {
            const parts = [];
            if (r?.category) parts.push(`${r.category}`);
            if (r?.cost != null)
              parts.push(`${r.cost}${currency ? ` ${currency}` : ""}`);
            if (r?.unit) parts.push(`${r.unit}`);
            return parts.join(" ");
          })
          .filter(Boolean)
          .join(", ");
      },
      renderCell: ({ value }) => <Box>{capitalizeFirstLetter(value)}</Box>,
    },

    {
      field: "qualifications",
      headerName: "Qualifications",
      flex: 0.7,
      type: "string",
      valueGetter: (params) =>
        (params.row?.qualifications || [])
          .map((q) => q?.type || q)
          .filter(Boolean)
          .join(", "),
      renderCell: ({ value }) => <Box>{capitalizeFirstLetter(value)}</Box>,
    },

    { field: "country", headerName: "Country", flex: 0.5, type: "string" },

    {
      field: "trusted",
      headerName: "Trusted",
      flex: 0.2,
      type: "string",
      valueGetter: (params) => (params.value ? "Yes" : "No"),
      renderCell: ({ value }) => <Box>{value}</Box>,
    },
    {
      field: "availability",
      headerName: "Availability",
      flex: 1,
      type: "string",
      valueGetter: (params) => {
        const arr = params.row.availability || [];
        if (!Array.isArray(arr) || arr.length === 0) return "";
        const enabled = arr.filter((a) => a && a.enabled);
        if (enabled.length === 0) return "";
        return enabled
          .map((a) => {
            const day = a.day
              ? a.day.slice(0, 3).charAt(0).toUpperCase() + a.day.slice(1, 3)
              : "";
            const start = a.startTime || "—";
            const end = a.endTime || "—";
            return `${day} ${start}–${end}`;
          })
          .join(", ");
      },
      renderCell: ({ value }) => (
        <Box sx={{ whiteSpace: "normal", lineHeight: 1.3 }}>{value || "—"}</Box>
      ),
      sortComparator: (a, b) => (a || "").localeCompare(b || ""),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.5,
      type: "dateTime",
      valueGetter: (params) => new Date(params.value),
      renderCell: ({ value }) =>
        value ? <Box>{value.toLocaleDateString()}</Box> : <Box></Box>,
    },
  ];

  const toggleColumnVisibility = (field) => {
    setColumnsVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const getColumns = () => {
    return columns.map((column) => ({
      ...column,
      hide: !columnsVisibility[column.field],
    }));
  };

  // Function to update filterModel, passed down to DataGridCustomToolbar
  const handleFilterModelChange = (newModel) => {
    setFilterModel(newModel);
  };

  if (!data || isLoading) return <Spinner />;

  return (
    <Box m="1rem 2.5rem 0rem 2.5rem">
      <SnackbarComponent />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px="2rem"
      >
        <Header title="Translators" subtitle="List of translators" />
        <ActionsMenu actions={actions} />
      </Box>
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        aria-labelledby="create-dialog-title"
        aria-describedby="create-dialog-description"
      >
        <Box
          sx={{
            margin: "1rem",
          }}
        >
          <DialogTitle
            sx={{
              fontSize: "1.5rem",
              paddingBottom: "2rem",
            }}
          >
            Create New Translator
          </DialogTitle>
          <DialogContent sx={{ width: "500px" }}>
            <DialogContentText
              id="confrim-dialog-description"
              sx={{
                marginBottom: "1rem",
              }}
            >
              Enter the translator's name and email address.
            </DialogContentText>
            {translatorExists ? (
              <DialogContentText
                color="error"
                sx={{
                  marginBottom: "2rem",
                }}
              >
                A vendor with this name already exists. Continue?
              </DialogContentText>
            ) : null}
            <TextField
              label="Name"
              sx={{
                marginBottom: "1rem",
              }}
              fullWidth
              value={newTranslatorData.name}
              onChange={(e) =>
                setNewTranslatorData({
                  ...newTranslatorData,
                  name: e.target.value,
                })
              }
            />
            <TextField
              label="Email"
              sx={{
                marginBottom: "1rem",
              }}
              fullWidth
              value={newTranslatorData.email}
              onChange={(e) =>
                setNewTranslatorData({
                  ...newTranslatorData,
                  email: e.target.value,
                })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleDialogConfirm();
              }}
              color="success"
            >
              {translatorExists ? "Continue" : "Create"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
            padding: "1rem",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={isLoading || !data}
          getRowId={(row) => row._id}
          rows={data.vendors || []}
          sx={{
            backgroundColor: theme.palette.background.alt,
            ".MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.background.alt,
              borderTop: "2px solid rgba(224, 224, 224, 1)",
              overflowX: "hidden",
            },
          }}
          columns={getColumns()}
          rowCount={(data && data.total) || 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{
            toolbar: {
              searchInput,
              setSearchInput,
              setSearch,
              columns,
              columnsVisibility,
              toggleColumnVisibility,
              onFilterModelChange: handleFilterModelChange,
            },
          }}
          onRowClick={(e) => {
            navigate("/translators/" + e.id);
          }}
        />
      </Box>
    </Box>
  );
};

export default Translators;
